import { toast } from 'sonner';
import { isFinishedIssueStatus, type Issue } from '@overlayqa/shared';
import { chipBaseClasses } from '../shared/badges/badgeBase';

export type NotDetectedVia = 'not_detected_chip';

/** The scan stopped finding this issue; the person, not the scan, decides it is fixed. */
export function NotDetectedChip({ issue, onMarkFixed, busy = false }: { issue: Pick<Issue, 'notDetectedAt' | 'status'>; onMarkFixed: () => void; busy?: boolean }) {
  if (!issue.notDetectedAt || isFinishedIssueStatus(issue.status)) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className={`${chipBaseClasses} whitespace-nowrap bg-emerald-50 text-emerald-600`} title="The page no longer shows this finding. You decide when it is fixed.">No longer detected</span>
      <button type="button" className="btn-ghost btn-sm whitespace-nowrap" onClick={onMarkFixed} disabled={busy}><span className="text-cap-trim">Mark fixed</span></button>
    </span>
  );
}

/**
 * Resolve through the caller's own status write, tagged `via`, then offer Undo
 * that writes the status the row held before the click (untagged: an undo is
 * not the chip gesture). The toast waits for the write: a write that returns a
 * promise must resolve before "Marked fixed" shows, and a rejection goes to
 * `onError` (with the status it tried to write) instead of a success toast.
 * Never rejects.
 */
export function markFixedWithUndo<S extends string>(
  previous: S,
  write: (status: S, via?: NotDetectedVia) => void | Promise<unknown>,
  onError?: (err: unknown, status: S) => void,
): Promise<void> {
  // Synchronous call (the optimistic write starts on the click), untagged when no `via`.
  const run = (status: S, via?: NotDetectedVia): Promise<unknown> => {
    try { return Promise.resolve(via ? write(status, via) : write(status)); } catch (err) { return Promise.reject(err); }
  };
  return run('resolved' as S, 'not_detected_chip').then(
    () => { toast.success('Marked fixed', { duration: 10000, action: { label: 'Undo', onClick: () => { run(previous).catch((err) => onError?.(err, previous)); } } }); },
    (err) => { onError?.(err, 'resolved' as S); },
  );
}
